/**
 * <s-personal-shopper> — stylist chat pop-up
 * Opens its <dialog> once per browsing session (and any time from the launcher
 * button), plays the stylist's intro bubbles with a typing indicator and asks
 * whether the visitor is shopping for herself or for a gift.
 *   - For herself: styles a random outfit from every category pool
 *     (<template data-ps-pool="role">) with a shuffle button.
 *   - For a gift: shows the gift card amounts (<ul data-ps-gift>).
 * Free-text questions are answered from the catalogue snapshot
 * (<script data-ps-catalog>) and the list of collections
 * (<script data-ps-collections>): categories, sizes, colours, brands,
 * price ranges, newest arrivals and gift card.
 *
 * Commands (on the dialog):
 *   --ps-me      — shopping for herself; show the reply and the look
 *   --ps-gift    — shopping for a gift; show the gift card options
 *   --ps-shuffle — pick a new random look
 */

const SIZE_PATTERN = /^(xxs|xs|s|m|l|xl|xxl|xxxl|2xl|3xl|tu|\d{2})$/;
const MAX_RESULTS = 6;

const STOPWORDS = new Set(`
  a ad al alla alle ai agli anche avete avere avreste c ce ci che chi con come cosa cose da dal dalla dei del della delle di do dove e ed esiste esistono gli ha hai hanno ho i il in io la le li lo ma me mi mio mia miei mie modello modelli ne nel nella non o per piu po poi qual quale quali qualche qualcosa quanto quanta quanti quante questo questa quello quella se si sia sono su sul sulla taglia taglie tagli misura misure numero numeri ti tra tu tua tuo un una uno vi vorrei voglio cerco cercavo serve servirebbe disponibile disponibili disponibilita ancora avrei mostra mostrami fammi vedere ciao salve buongiorno buonasera grazie favore sapere dirmi dimmi potete puoi
  capo capi prodotto prodotti articolo articoli roba tipo tipi genere euro eur prezzo prezzi costa costano costo colore colori
  lei lui sua suo moglie mamma madre amica amiche sorella figlia fidanzata ragazza compagna nonna zia suocera collega donna donne
  bello bella belli belle carino carina carini carine elegante eleganti qualcuno qualcuna
  an and any are available can do does for have i in is it me my of on or please show size sizes the there to what which with you your hi hello thanks
`.trim().split(/\s+/));

const CATEGORY_GROUPS = [
  ['giacc', 'giubb', 'cappott', 'blazer', 'piumin', 'jacket', 'jakk', 'coat', 'trench', 'gilet', 'capospall', 'bomber', 'caban'],
  ['magli', 'pull', 'cardig', 'felp', 'sweat', 'knit', 'dolcevit', 'golf'],
  ['pantal', 'jeans', 'trouser', 'pants', 'legging'],
  ['gonn', 'skirt'],
  ['scarp', 'sneak', 'stival', 'sandal', 'mocass', 'decollet', 'shoe', 'boot', 'ballerin', 'calzatur'],
  ['abit', 'vestit', 'dress'],
  ['camic', 'top', 'blus', 'shirt', 'canott', 'tshirt'],
  ['bors', 'bag', 'pochette', 'accessor', 'zain'],
];

const PRICE_UNDER = new Set(['sotto', 'meno', 'entro', 'max', 'massimo', 'massima', 'fino', 'budget', 'under', 'below', 'less']);
const PRICE_OVER = new Set(['sopra', 'oltre', 'piu', 'over', 'above', 'more']);
const SIZE_WORDS = new Set(['taglia', 'taglie', 'numero', 'misura', 'size', 'n']);
const CHEAP_STEMS = ['economic', 'convenient', 'cheap', 'saldi', 'offert', 'sconto', 'scontat'];
const NEW_STEMS = ['nuov', 'novit', 'arriv', 'ultim', 'new'];
const OVERVIEW_STEMS = ['categori', 'collezion', 'vendete', 'reparti', 'tutto', 'catalog'];
const GIFT_TOKENS = ['regal', 'buono', 'buoni', 'gift', 'giftcard'];

const normalize = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const words = (value) => normalize(value).split(/[^a-z0-9]+/).filter(Boolean);
const startsWithAny = (token, stems) => stems.some((stem) => token.startsWith(stem));

class SPersonalShopper extends HTMLElement {
  #ac;
  #dialog;
  #timer;
  #started = false;
  #chosen = null;
  #lookShown = false;
  #busy = false;
  #lastPicks = {};
  #reduced = false;
  #catalog = null;
  #collections = null;
  #actions = 'none';
  #formShown = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    ['intro', 'reply', 'gift', 'gift-after', 'ask'].forEach((step) => this.#steps(step).forEach((el) => { el.hidden = true; }));
    this.#restore();

    this.#dialog.addEventListener('command', (e) => {
      if (e.command === '--ps-me' || e.command === '--ps-yes') {
        e.preventDefault();
        this.#forMe();
      } else if (e.command === '--ps-gift') {
        e.preventDefault();
        this.#forGift();
      } else if (e.command === '--ps-shuffle') {
        e.preventDefault();
        this.#renderLook(true);
      }
    }, opts);

    // Fires for both the automatic opening and the always-visible launcher button.
    this.#dialog.addEventListener('toggle', (e) => {
      if (e.newState === 'open') {
        try { sessionStorage.setItem(this.dataset.storageKey, '1'); } catch { /* storage blocked */ }
        clearTimeout(this.#timer);
        requestAnimationFrame(() => requestAnimationFrame(() => this.#dialog.classList.add('is-revealed')));
        if (this.#started) this.#revealLast();
        this.#playIntro();
      } else {
        this.#dialog.classList.remove('is-revealed');
      }
    }, opts);

    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.querySelector('[data-ps-ask]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = e.currentTarget.elements.q;
      const question = input.value.trim();
      if (!question || this.#busy) return;
      input.value = '';
      this.#answer(question);
    }, opts);

    const key = this.dataset.storageKey;
    let seen = false;
    try { seen = sessionStorage.getItem(key) === '1'; } catch { /* storage blocked */ }
    if (!seen) this.#timer = setTimeout(() => this.#open(), 900);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #steps(name) {
    return [...this.querySelectorAll(`[data-ps-step="${name}"]`)];
  }

  #wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, this.#reduced ? 0 : ms));
  }

  #reveal(node) {
    node.scrollIntoView({ block: 'nearest', behavior: this.#reduced ? 'auto' : 'smooth' });
  }

  #open() {
    if (document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
  }

  async #typing(container, text) {
    const typing = this.querySelector('[data-ps-typing]');
    container.append(typing);
    typing.hidden = false;
    this.#reveal(typing);
    this.#dialog.classList.add('is-talking');
    await this.#wait(Math.min(700 + (text || '').trim().length * 14, 1900));
    typing.hidden = true;
    this.#dialog.classList.remove('is-talking');
  }

  async #say(el, container = this.querySelector('[data-ps-log]')) {
    container.append(el);
    el.hidden = true;
    await this.#typing(container, el.textContent);
    el.hidden = false;
    el.classList.add('ps-in');
    this.#reveal(el);
    await this.#wait(250);
  }

  async #playIntro() {
    if (this.#started) return;
    this.#started = true;
    await this.#wait(1100);
    for (const el of this.#steps('intro')) await this.#say(el);
    this.#showActions('ask', false);
    this.#save();
  }

  // ---- Remember the conversation across pages (same browsing session) -------

  get #chatKey() {
    return `${this.dataset.storageKey}-chat`;
  }

  #save() {
    const look = this.querySelector('[data-ps-look]');
    const gift = this.querySelector('[data-ps-gift]');
    const state = {
      log: this.querySelector('[data-ps-log]')?.innerHTML || '',
      look: look?.innerHTML || '',
      lookHidden: look ? look.hidden : true,
      lookInQa: Boolean(look?.closest('[data-ps-qa]')),
      giftHidden: gift ? gift.hidden : true,
      qa: this.querySelector('[data-ps-qa]')?.innerHTML || '',
      actions: this.#actions,
      form: this.#formShown,
      chosen: this.#chosen,
      lookShown: this.#lookShown,
      lastPicks: this.#lastPicks,
    };
    try { sessionStorage.setItem(this.#chatKey, JSON.stringify(state)); } catch { /* storage full or blocked */ }
  }

  #restore() {
    let state = null;
    try { state = JSON.parse(sessionStorage.getItem(this.#chatKey) || 'null'); } catch { state = null; }
    if (!state || !state.log) return;
    const log = this.querySelector('[data-ps-log]');
    const look = this.querySelector('[data-ps-look]');
    const gift = this.querySelector('[data-ps-gift]');
    const qa = this.querySelector('[data-ps-qa]');
    if (log) log.innerHTML = state.log;
    if (gift) gift.hidden = state.giftHidden;
    if (state.lookInQa) {
      // The look was moved into the conversation; the saved chat already contains it.
      look?.remove();
    } else if (look) {
      look.innerHTML = state.look;
      look.hidden = state.lookHidden;
    }
    if (qa) qa.innerHTML = state.qa;
    // Restored bubbles appear at once, without replaying their entrance animation.
    this.querySelectorAll('[data-ps-log] .ps-in, [data-ps-look] .ps-in, [data-ps-qa] .ps-in').forEach((el) => el.classList.remove('ps-in'));
    const typing = this.querySelector('[data-ps-typing]');
    if (typing) typing.hidden = true;
    this.#chosen = state.chosen;
    this.#lookShown = Boolean(state.lookShown);
    this.#lastPicks = state.lastPicks || {};
    this.#started = true;
    this.#showActions(state.actions || 'none', Boolean(state.form));
  }

  #revealLast() {
    const containers = ['[data-ps-qa]', '[data-ps-gift]', '[data-ps-look]', '[data-ps-log]']
      .map((selector) => this.querySelector(selector))
      .filter((el) => el && !el.hidden && el.lastElementChild);
    const last = containers[0]?.lastElementChild;
    if (last) requestAnimationFrame(() => last.scrollIntoView({ block: 'end', behavior: 'auto' }));
  }

  #showActions(name, withForm = true) {
    this.#actions = name;
    if (withForm) this.#formShown = true;
    this.querySelectorAll('[data-ps-actions]').forEach((el) => {
      const wasHidden = el.hidden;
      // The "for me / a gift" choice stays available after the intro, next to the step's own buttons.
      el.hidden = el.dataset.psActions === 'ask' ? name === 'none' : el.dataset.psActions !== name;
      if (!el.hidden && wasHidden) el.classList.add('ps-in');
    });
    const form = this.querySelector('[data-ps-ask]');
    if (withForm && form && form.hidden) {
      form.hidden = false;
      form.classList.add('ps-in');
    }
  }

  #clone(name) {
    return this.querySelector(`template[data-ps-tpl="${name}"]`).content.firstElementChild.cloneNode(true);
  }

  #userBubble(text, container) {
    const bubble = this.#clone('user');
    bubble.textContent = text;
    container.append(bubble);
    this.#reveal(bubble);
  }

  async #askPrompt() {
    if (this.#chosen === 'done') return;
    this.#chosen = 'done';
    for (const el of this.#steps('ask')) await this.#say(el, this.querySelector('[data-ps-qa]'));
  }

  // A fresh, visible copy of a caller-authored bubble, for repeating it later in the chat.
  #copyStep(el) {
    const copy = el.cloneNode(true);
    copy.removeAttribute('data-ps-step');
    copy.removeAttribute('data-ps-whatsapp');
    copy.classList.remove('ps-in');
    return copy;
  }

  // The choice can be made any number of times: the first answer plays the authored
  // bubbles in place, later ones continue the conversation at the bottom of the chat.
  async #forMe() {
    if (this.#busy) return;
    this.#busy = true;
    const first = this.#chosen === null;
    if (first) this.#chosen = 'me';
    this.#showActions('none', !first);
    const qa = this.querySelector('[data-ps-qa]');

    if (first) {
      this.#userBubble(this.dataset.meLabel || '', this.querySelector('[data-ps-log]'));
      await this.#wait(300);
      for (const el of this.#steps('reply')) await this.#say(el);
      await this.#renderLook(false);
    } else {
      this.#userBubble(this.dataset.meLabel || '', qa);
      await this.#wait(300);
      for (const el of this.#steps('reply')) await this.#say(this.#copyStep(el), qa);
      // Bring the look to the end of the conversation so the new outfit appears below the latest message.
      const look = this.querySelector('[data-ps-look]');
      if (look) qa.append(look);
      await this.#renderLook(false);
      if (look) this.#reveal(look);
    }

    this.#lookShown = true;
    this.#showActions('look');
    await this.#askPrompt();
    this.#save();
    this.#busy = false;
  }

  async #forGift() {
    if (this.#busy) return;
    this.#busy = true;
    const first = this.#chosen === null;
    if (first) this.#chosen = 'gift';
    this.#showActions('none', !first);
    const qa = this.querySelector('[data-ps-qa]');
    const gift = this.querySelector('[data-ps-gift]');

    if (first) {
      this.#userBubble(this.dataset.giftLabel || '', this.querySelector('[data-ps-log]'));
      await this.#wait(300);
      for (const el of this.#steps('gift')) await this.#say(el);
      if (gift) {
        gift.hidden = false;
        this.#reveal(gift);
        await this.#wait(400);
      }
      for (const el of this.#steps('gift-after')) await this.#say(el, qa);
      this.#chosen = 'done';
    } else {
      this.#userBubble(this.dataset.giftLabel || '', qa);
      await this.#wait(300);
      for (const el of this.#steps('gift')) await this.#say(this.#copyStep(el), qa);
      if (gift) {
        const list = gift.cloneNode(true);
        list.removeAttribute('data-ps-gift');
        list.hidden = false;
        qa.append(list);
        this.#reveal(list);
        await this.#wait(400);
      }
      for (const el of this.#steps('gift-after')) await this.#say(this.#copyStep(el), qa);
    }

    this.#showActions('gift');
    this.#save();
    this.#busy = false;
  }

  // ---- Outfit from every category --------------------------------------------

  #pool(role) {
    const template = this.querySelector(`template[data-ps-pool="${role}"]`);
    return template ? [...template.content.children] : [];
  }

  #lookRoles() {
    const has = (role) => this.#pool(role).length > 0;
    const random = (list) => list[Math.floor(Math.random() * list.length)];
    const dressLook = has('dress') && (!has('bottom') || Math.random() < 0.4);
    const upper = ['knit', 'top'].filter(has);
    const roles = dressLook
      ? ['outer', 'dress', 'shoes', 'bag']
      : ['outer', upper.length ? random(upper) : null, 'bottom', 'shoes', 'bag'];
    return roles.filter((role) => role && has(role));
  }

  async #renderLook(isShuffle) {
    const look = this.querySelector('[data-ps-look]');
    const slots = [...look.querySelectorAll('[data-ps-slot]')];
    const roles = this.#lookRoles();
    if (isShuffle) this.#dialog.classList.add('is-talking');
    look.hidden = false;

    for (const [index, slot] of slots.entries()) {
      const role = roles[index];
      if (!role) { slot.replaceChildren(); continue; }
      const items = this.#pool(role);
      let pick = Math.floor(Math.random() * items.length);
      if (items.length > 1 && pick === this.#lastPicks[role]) pick = (pick + 1) % items.length;
      this.#lastPicks[role] = pick;
      const node = items[pick].cloneNode(true);
      node.classList.add('ps-in');
      slot.replaceChildren(node);
      await this.#wait(140);
    }
    this.#dialog.classList.remove('is-talking');
    if (isShuffle) this.#save();
  }

  // ---- Q&A about the whole shop ---------------------------------------------

  #json(selector) {
    try { return JSON.parse(this.querySelector(selector)?.textContent || '[]'); } catch { return []; }
  }

  #getCatalog() {
    if (this.#catalog) return this.#catalog;
    this.#catalog = this.#json('[data-ps-catalog]').map((item) => ({
      ...item,
      titleWords: words(item.t),
      keyWords: words(item.k),
      descWords: words(item.d),
      sizes: this.#sizesOf(item),
    }));
    return this.#catalog;
  }

  #getCollections() {
    if (this.#collections) return this.#collections;
    this.#collections = this.#json('[data-ps-collections]').map((c) => ({ ...c, titleWords: words(c.t) }));
    return this.#collections;
  }

  #sizesOf(item) {
    if (item.one) return [{ label: this.dataset.oneSize, available: item.v.some((v) => v.a), one: true }];
    const map = new Map();
    for (const variant of item.v) {
      const label = String(variant.s || '').trim();
      if (!label) continue;
      map.set(label, Boolean(map.get(label)) || Boolean(variant.a));
    }
    return [...map].map(([label, available]) => ({ label, available }));
  }

  #parse(question) {
    const cleaned = normalize(question)
      .replace(/€/g, ' euro ')
      .replace(/(\d)[.,](\d{2})\b/g, '$1')
      .replace(/\b(l|un|dell|nell|all|sull|dall|c|d|quell|qualcos|po)'/g, ' ');
    const tokens = cleaned.split(/[^a-z0-9]+/).filter(Boolean);
    const intent = { size: null, terms: [], min: null, max: null, cheap: false, newest: false, overview: false, gift: false, raw: [] };

    tokens.forEach((token, index) => {
      const previous = tokens[index - 1];
      const next = tokens[index + 1];

      if (GIFT_TOKENS.some((stem) => token.startsWith(stem)) || (token === 'carta' && next && next.startsWith('regal'))) { intent.gift = true; return; }
      if (startsWithAny(token, CHEAP_STEMS)) { intent.cheap = true; return; }
      if (startsWithAny(token, NEW_STEMS)) { intent.newest = true; return; }
      if (startsWithAny(token, OVERVIEW_STEMS)) { intent.overview = true; return; }

      if (/^\d+$/.test(token)) {
        const value = Number(token);
        let direction = null;
        if (!SIZE_WORDS.has(previous)) {
          for (let back = index - 1; back >= Math.max(0, index - 3); back -= 1) {
            const word = tokens[back];
            if (/^\d+$/.test(word) || SIZE_WORDS.has(word)) break;
            if (PRICE_UNDER.has(word)) { direction = 'max'; break; }
            if (PRICE_OVER.has(word)) { direction = 'min'; break; }
          }
          if (!direction && (next === 'euro' || next === 'eur' || token.length >= 3)) direction = 'max';
        }
        if (direction) { intent[direction] = value; return; }
      }

      if (token === 'unica' && previous === 'taglia') { intent.size = 'tu'; return; }
      if (SIZE_PATTERN.test(token)) { intent.size = token; return; }
      if (STOPWORDS.has(token) || token.length < 3 || PRICE_UNDER.has(token) || PRICE_OVER.has(token)) return;
      const group = CATEGORY_GROUPS.find((stems) => stems.some((stem) => token.startsWith(stem) || (token.length >= 4 && stem.startsWith(token))));
      intent.raw.push(token);
      if (group) intent.terms.push({ stems: group, category: true, token });
      else intent.terms.push({ stems: [token.length >= 5 ? token.replace(/[aeio]$/, '') : token], category: false, token });
    });

    return intent;
  }

  #score(product, terms) {
    const hits = (list, stems) => list.some((word) => stems.some((stem) => word.startsWith(stem)));
    let score = 0;
    for (const term of terms) {
      if (hits(product.titleWords, term.stems)) score += 3;
      else if (hits(product.keyWords, term.stems)) score += 2;
      else if (hits(product.descWords, term.stems)) score += 1;
      else if (term.category) return 0;
    }
    return score;
  }

  #hasSize(product, size) {
    return product.sizes.some((entry) => {
      if (!entry.available) return false;
      const label = normalize(entry.label);
      const isOneSize = entry.one || /^(un|uni|unica|tu|taglia unica|one size|os)$/.test(label);
      if (size === 'tu') return isOneSize;
      // A one-size piece fits the usual letter sizes, but not shoe numbers.
      if (isOneSize && /^[a-z]+$/.test(size)) return true;
      return label === size || words(label).includes(size);
    });
  }

  #isGiftCard(product) {
    return this.dataset.giftUrl && product.u === this.dataset.giftUrl;
  }

  #collectionLinks(terms) {
    const links = [];
    for (const term of terms.filter((t) => t.category)) {
      for (const collection of this.#getCollections()) {
        if (collection.titleWords.some((word) => term.stems.some((stem) => word.startsWith(stem)))) {
          if (!links.some((link) => link.url === collection.u)) {
            links.push({ url: collection.u, label: (this.dataset.seeCollection || '%name%').replace('%name%', collection.t) });
          }
        }
      }
    }
    return links;
  }

  #overviewLinks() {
    const links = this.#getCollections().map((collection) => ({ url: collection.u, label: collection.t }));
    if (this.dataset.giftUrl) links.push({ url: this.dataset.giftUrl, label: this.dataset.giftCardLink });
    return links;
  }

  #search(question) {
    const intent = this.#parse(question);
    const { size, terms, min, max, cheap, newest, gift } = intent;
    const hasPrice = min !== null || max !== null;
    const sizeLabel = size === 'tu' ? this.dataset.oneSize : (size || '').toUpperCase();
    const priceLabel = `${max !== null ? max : min} €`;
    const format = (template) => (template || '')
      .replace('%size%', sizeLabel)
      .replace('%price%', priceLabel)
      .replace('%name%', intent.raw.join(' '));

    // Gift only ("cerco un regalo", "avete buoni regalo?"): suggest the gift card.
    if (gift && !terms.length && !size && !hasPrice) {
      return { text: this.dataset.answerGift, products: [], links: [], giftCard: true };
    }

    // Nothing specific ("cosa vendete?", "che categorie avete?"): list every category.
    if (!terms.length && !size && !hasPrice && !cheap && !newest) {
      return { text: this.dataset.answerOverview, products: [], links: this.#overviewLinks() };
    }

    const catalog = this.#getCatalog().filter((product) => !this.#isGiftCard(product));
    let matches = catalog;
    if (terms.length) {
      const scored = catalog
        .map((product) => ({ product, score: this.#score(product, terms) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score);
      const best = scored[0]?.score || 0;
      matches = scored.filter((entry) => entry.score >= best / 2).map((entry) => entry.product);
    }

    const links = this.#collectionLinks(terms);

    if (!matches.length) {
      const emptyCategory = terms.some((t) => t.category);
      return {
        text: emptyCategory ? format(this.dataset.answerEmptyCategory) : this.dataset.answerNone,
        products: [],
        links: this.#overviewLinks(),
        noMatch: true,
      };
    }

    let text = this.dataset.answerFound;

    if (size) {
      const withSize = matches.filter((product) => this.#hasSize(product, size));
      if (!withSize.length) {
        return { text: format(this.dataset.answerSizeNone), products: matches.slice(0, MAX_RESULTS), links };
      }
      matches = withSize;
      text = format(this.dataset.answerSize);
    }

    if (hasPrice) {
      const inRange = matches.filter((product) => (max === null || product.pc <= max * 100) && (min === null || product.pc >= min * 100));
      if (inRange.length) {
        matches = inRange.sort((a, b) => a.pc - b.pc);
        if (!size) text = format(max !== null ? this.dataset.answerPriceUnder : this.dataset.answerPriceOver);
      } else {
        const target = (max !== null ? max : min) * 100;
        matches = [...matches].sort((a, b) => Math.abs(a.pc - target) - Math.abs(b.pc - target));
        text = format(this.dataset.answerPriceNone);
      }
    } else if (cheap) {
      matches = [...matches].sort((a, b) => a.pc - b.pc);
      if (!size) text = this.dataset.answerCheap;
    } else if (newest) {
      matches = [...matches].sort((a, b) => Number(b.ts) - Number(a.ts));
      if (!size) text = this.dataset.answerNew;
    }

    if (gift) {
      text = this.dataset.answerGiftMix;
      if (this.dataset.giftUrl) links.push({ url: this.dataset.giftUrl, label: this.dataset.giftCardLink });
    }

    if (matches.length > MAX_RESULTS && terms.length && this.dataset.searchUrl) {
      const query = intent.raw.join(' ');
      links.push({ url: `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`, label: this.dataset.seeAll });
    }

    return { text, products: matches.slice(0, MAX_RESULTS), links };
  }

  #row(product) {
    const row = this.#clone('row');
    row.querySelector('[data-ps-link]').href = product.u;
    const img = row.querySelector('[data-ps-img]');
    if (product.i) img.src = product.i;
    else img.remove();
    row.querySelector('[data-ps-title]').textContent = product.t;
    row.querySelector('[data-ps-price]').textContent = product.p;
    const sizes = row.querySelector('[data-ps-sizes]');
    for (const entry of product.sizes) {
      const chip = this.#clone(entry.available ? 'chip' : 'chip-off');
      chip.textContent = entry.label;
      if (!entry.available) chip.setAttribute('aria-label', `${entry.label} ${this.dataset.soldOut}`);
      sizes.append(chip);
    }
    return row;
  }

  async #answer(question) {
    this.#busy = true;
    const container = this.querySelector('[data-ps-qa]');
    this.#userBubble(question, container);
    const { text, products, links, giftCard, noMatch } = this.#search(question);
    await this.#wait(250);
    await this.#typing(container, text);

    const bubble = this.#clone('bot');
    bubble.querySelector('[data-ps-text]').textContent = text;
    const list = bubble.querySelector('[data-ps-list]');
    const giftList = this.querySelector('[data-ps-gift]');
    if (products.length) {
      products.forEach((product) => list.append(this.#row(product)));
    } else if (giftCard && giftList) {
      [...giftList.children].forEach((item) => list.append(item.cloneNode(true)));
    } else {
      list.remove();
    }

    const linkBox = bubble.querySelector('[data-ps-links]');
    if (links.length) {
      for (const link of links) {
        const pill = this.#clone('pill');
        pill.href = link.url;
        pill.textContent = link.label;
        linkBox.append(pill);
      }
    } else {
      linkBox.remove();
    }

    container.append(bubble);
    this.#reveal(bubble);

    // No match: point the visitor to WhatsApp for direct advice.
    const whatsapp = this.querySelector('[data-ps-whatsapp]');
    if (noMatch && whatsapp) {
      const copy = whatsapp.cloneNode(true);
      copy.removeAttribute('data-ps-step');
      copy.removeAttribute('data-ps-whatsapp');
      copy.hidden = false;
      copy.classList.add('ps-in');
      await this.#wait(400);
      container.append(copy);
      this.#reveal(copy);
    }
    this.#save();
    this.#busy = false;
  }
}

if (!customElements.get('s-personal-shopper')) {
  customElements.define('s-personal-shopper', SPersonalShopper);
}
